import { type ReactNode, useCallback, useState } from 'react';

import type {
  UseBottomDrawerOptions,
  UseBottomDrawerReturn,
} from '../BottomDrawer.types';

/**
 * Hook for encapsulated state management of BottomDrawer.
 * Provides a clean API for opening/closing the drawer with dynamic content.
 *
 * @example
 * ```tsx
 * const drawer = useBottomDrawer({
 *   onClose: () => console.log('Drawer closed'),
 * });
 *
 * // Open with content
 * drawer.open(<SignupContent onSuccess={drawer.close} />);
 *
 * // Close programmatically
 * drawer.close();
 *
 * // Render the drawer
 * <BottomDrawer {...drawer.drawerProps} />
 * ```
 */
export function useBottomDrawer(
  options?: UseBottomDrawerOptions
): UseBottomDrawerReturn {
  const [state, setState] = useState<{
    isOpen: boolean;
    content: ReactNode | null;
  }>({
    isOpen: false,
    content: null,
  });

  const open = useCallback((content: ReactNode) => {
    setState({ isOpen: true, content });
  }, []);

  const close = useCallback(() => {
    setState({ isOpen: false, content: null });
    options?.onClose?.();
  }, [options]);

  return {
    isOpen: state.isOpen,
    content: state.content,
    open,
    close,
    drawerProps: {
      isOpen: state.isOpen,
      onClose: close,
      children: state.content,
    },
  };
}

export default useBottomDrawer;
